import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { formatDateShort } from '../utils/date';
import { formatStatementDescription, invoiceStatementLines } from '../utils/clientBillingCalculation';

interface InvoicePreviewProps {
  open: boolean;
  onClose: () => void;
  invoice: any;
  onGenerate?: () => void;
  allowRateEditing?: boolean;
  onInvoiceUpdated?: (updatedInvoice: any) => void;
}

const InvoicePreview: React.FC<InvoicePreviewProps> = ({ 
  open, 
  onClose, 
  invoice, 
  onGenerate, 
  allowRateEditing = false,
  onInvoiceUpdated 
}) => {
  const { user } = useAuth();
  const [editingItem, setEditingItem] = useState<number | null>(null);
  const [editedRates, setEditedRates] = useState<{[key: number]: number}>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [localInvoice, setLocalInvoice] = useState<any>(null);

  // Sync local invoice state with prop
  useEffect(() => {
    if (invoice) {
      setLocalInvoice({ ...invoice });
    }
  }, [invoice]);

  const handleEditRate = (itemIndex: number, currentRate: number) => {
    setEditingItem(itemIndex);
    setEditedRates({ ...editedRates, [itemIndex]: currentRate });
  };

  const handleSaveRate = async (itemIndex: number) => {
    const newRate = editedRates[itemIndex];
    if (newRate === undefined || newRate < 0) {
      setError('Please enter a valid rate');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Update the local invoice state
      const updatedItems = [...localInvoice.items];
      updatedItems[itemIndex] = {
        ...updatedItems[itemIndex],
        rate: newRate,
        amount: newRate * updatedItems[itemIndex].quantity
      };

      // Recalculate totals
      const totalBilling = updatedItems.reduce((sum, item) => sum + item.amount, 0);
      const netAmount = (localInvoice.opening_balance || 0) + totalBilling - (localInvoice.total_payments || 0);

      const updatedInvoice = {
        ...localInvoice,
        items: updatedItems,
        total_billing: totalBilling,
        net_amount: netAmount
      };

      setLocalInvoice(updatedInvoice);
      setEditingItem(null);
      setSuccess('Rate updated successfully');

      // Notify parent component if callback provided
      if (onInvoiceUpdated) {
        onInvoiceUpdated(updatedInvoice);
      }
    } catch (err: any) {
      setError('Failed to update rate');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = (itemIndex: number) => {
    setEditingItem(null);
    const newEditedRates = { ...editedRates };
    delete newEditedRates[itemIndex];
    setEditedRates(newEditedRates);
  };

  const handleRateChange = (itemIndex: number, value: string) => {
    const numericValue = parseFloat(value) || 0;
    setEditedRates({ ...editedRates, [itemIndex]: numericValue });
  };

  const formatCurrency = (amount: number, currency?: string) => {
    const currencyCode = currency || invoice?.currency || user?.currency || 'USD';
    const locale = currencyCode === 'INR' ? 'en-IN' : 'en-US';
    
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
    }).format(amount || 0);
  };

  const numberToWords = (num: number, currency?: string): string => {
    const currencyCode = currency || invoice?.currency || user?.currency || 'USD';
    
    if (num === 0) return `Zero ${currencyCode === 'INR' ? 'Rupees' : 'Dollars'}`;
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    const convertLessThanOneThousand = (num: number): string => {
      if (num === 0) return '';
      
      if (num < 10) return ones[num];
      if (num < 20) return teens[num - 10];
      if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 !== 0 ? ' ' + ones[num % 10] : '');
      return ones[Math.floor(num / 100)] + ' Hundred' + (num % 100 !== 0 ? ' ' + convertLessThanOneThousand(num % 100) : '');
    };
    
    const convert = (num: number): string => {
      if (num === 0) return 'Zero';
      if (num < 1000) return convertLessThanOneThousand(num);
      if (num < 1000000) return convertLessThanOneThousand(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 !== 0 ? ' ' + convertLessThanOneThousand(num % 1000) : '');
      return convertLessThanOneThousand(Math.floor(num / 1000000)) + ' Million' + (num % 1000000 !== 0 ? ' ' + convert(num % 1000000) : '');
    };
    
    const dollars = Math.floor(num);
    const cents = Math.round((num - dollars) * 100);
    
    const currencyName = currencyCode === 'INR' ? 'Rupee' : 'Dollar';
    const currencyPlural = currencyCode === 'INR' ? 'Rupees' : 'Dollars';
    const centName = currencyCode === 'INR' ? 'Paisa' : 'Cent';
    const centPlural = currencyCode === 'INR' ? 'Paise' : 'Cents';
    
    let result = convert(dollars) + ' ' + (dollars !== 1 ? currencyPlural : currencyName);
    if (cents > 0) {
      result += ' and ' + convert(cents) + ' ' + (cents !== 1 ? centPlural : centName);
    }
    
    return result;
  };

  // Using formatDateShort from utils/date for New York timezone
  const formatDate = (dateString: string) => formatDateShort(dateString);

  if (!localInvoice) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Invoice Preview
      </DialogTitle>
      <DialogContent>
        {/* Error and Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        <Paper sx={{ p: 3, fontFamily: 'Arial, sans-serif' }}>
          {/* Header */}
          <Box sx={{ 
            backgroundColor: '#f5f5f5', 
            p: 2, 
            mb: 3, 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
              Invoice
            </Typography>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Ref No.: {localInvoice.invoice_number || 'N/A'}
              </Typography>
              <Typography variant="body2">
                Date: {formatDate(localInvoice.invoice_date)}
              </Typography>
            </Box>
          </Box>

          {/* Billed party */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                {localInvoice.clientFirm?.name || localInvoice.vendorFirm?.name || 'Firm Name'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {(localInvoice.clientFirm || localInvoice.vendorFirm)?.address_1 || 'Address Line 1'}
                {(localInvoice.clientFirm || localInvoice.vendorFirm)?.address_2 && `, ${(localInvoice.clientFirm || localInvoice.vendorFirm).address_2}`}
                {(localInvoice.clientFirm || localInvoice.vendorFirm)?.city && `, ${(localInvoice.clientFirm || localInvoice.vendorFirm).city}`}
                {(localInvoice.clientFirm || localInvoice.vendorFirm)?.state && `, ${(localInvoice.clientFirm || localInvoice.vendorFirm).state}`}
                {(localInvoice.clientFirm || localInvoice.vendorFirm)?.zip_code && ` ${(localInvoice.clientFirm || localInvoice.vendorFirm).zip_code}`}
                {(localInvoice.clientFirm || localInvoice.vendorFirm)?.country && `, ${(localInvoice.clientFirm || localInvoice.vendorFirm).country}`}
              </Typography>
              {(localInvoice.case?.title || localInvoice.case_title) && (
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                  Case: {localInvoice.case?.title || localInvoice.case_title}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Invoice Details Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: allowRateEditing ? '35%' : '40%' }}>Activity Period</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: allowRateEditing ? '25%' : '30%' }}>Amount</TableCell>
                  {allowRateEditing && (
                    <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Actions</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 'bold', width: allowRateEditing ? '25%' : '30%' }}>Total Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Activity Period */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Activity Period:</TableCell>
                  <TableCell>
                    {formatDate(localInvoice.period_start)} To {formatDate(localInvoice.period_end)}
                  </TableCell>
                  {allowRateEditing && <TableCell></TableCell>}
                  <TableCell></TableCell>
                </TableRow>

                {/* Opening Balance */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Opening Balance:</TableCell>
                  <TableCell></TableCell>
                  {allowRateEditing && <TableCell></TableCell>}
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(localInvoice.opening_balance || 0, localInvoice.currency)}
                  </TableCell>
                </TableRow>

                {/* Payments Section */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>(-) Payments:</TableCell>
                  <TableCell></TableCell>
                  {allowRateEditing && <TableCell></TableCell>}
                  <TableCell></TableCell>
                </TableRow>

                {/* Payment Details Header */}
                <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                  <TableCell sx={{ pl: 4, fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Payment Type</TableCell>
                  {allowRateEditing && <TableCell></TableCell>}
                  <TableCell sx={{ fontWeight: 'bold' }}>Reference #</TableCell>
                </TableRow>

                {/* Payment Details (if any) */}
                {localInvoice.total_payments > 0 && (
                  <TableRow>
                    <TableCell sx={{ pl: 4 }}>{formatDate(localInvoice.invoice_date)}</TableCell>
                    <TableCell>Payment</TableCell>
                    {allowRateEditing && <TableCell></TableCell>}
                    <TableCell>{localInvoice.invoice_number}</TableCell>
                  </TableRow>
                )}

                {/* Current Billing */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>(+) Current Billing:</TableCell>
                  <TableCell></TableCell>
                  {allowRateEditing && <TableCell></TableCell>}
                  <TableCell></TableCell>
                </TableRow>

                {/* Invoice Items */}
                {localInvoice.items?.map((item: any, index: number) => {
                  const estimate = item.clientEstimate || item.client_estimate || item.caseEstimate;
                  const statementLines = invoiceStatementLines(estimate);
                  const detailRows: Array<{ description: string; amount: number | null; emphasis?: boolean }> = statementLines.length > 0
                    ? [
                        ...statementLines.map((line) => ({
                          description: formatStatementDescription(line),
                          amount: line.amount,
                        })),
                        {
                          description: 'Subtotal',
                          amount: Number(item.amount),
                          emphasis: true,
                        },
                      ]
                    : [{
                        description: `Qty: ${item.quantity}`,
                        amount: Number(item.amount),
                      }];

                  return detailRows.map((row, rowIndex) => (
                    <TableRow key={`${index}-${rowIndex}`} sx={row.emphasis ? { backgroundColor: '#f5f5f5' } : undefined}>
                      <TableCell sx={{ pl: 4 }}>
                        {rowIndex === 0 && (
                          <>
                            {item.case_title || 'Case Title'}
                            <br />
                          </>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={row.emphasis ? { fontWeight: 'bold' } : undefined}>
                          {item.billing_type} - {row.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {rowIndex === 0 && editingItem === index ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              size="small"
                              type="number"
                              value={editedRates[index] || item.rate}
                              onChange={(e) => handleRateChange(index, e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }}
                              sx={{ width: 100 }}
                            />
                            <Typography variant="caption">
                              x {item.quantity}
                            </Typography>
                          </Box>
                        ) : (
                          ''
                        )}
                      </TableCell>
                      {allowRateEditing && (
                        <TableCell>
                          {rowIndex === 0 && (
                            editingItem === index ? (
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title="Save">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleSaveRate(index)}
                                    disabled={loading}
                                  >
                                    <SaveIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Cancel">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCancelEdit(index)}
                                    disabled={loading}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ) : (
                              <Tooltip title="Edit Rate">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditRate(index, item.rate)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )
                          )}
                        </TableCell>
                      )}
                      <TableCell sx={row.emphasis ? { fontWeight: 'bold' } : undefined}>
                        {row.amount == null ? '' : formatCurrency(row.amount, localInvoice.currency)}
                      </TableCell>
                    </TableRow>
                  ));
                })}

                {/* Total */}
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                  <TableCell></TableCell>
                  {allowRateEditing && <TableCell></TableCell>}
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(localInvoice.net_amount || 0, localInvoice.currency)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Amount in Words */}
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Amount in Words: {numberToWords(localInvoice.net_amount || 0, localInvoice.currency)}
            </Typography>
          </Box>

          {/* Summary Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mt: 3 }}>
            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#e3f2fd' }}>
              <Typography variant="h6" color="primary">
                {formatCurrency(localInvoice.total_billing || 0, localInvoice.currency)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Billing
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#fff3e0' }}>
              <Typography variant="h6" color="warning.main">
                {formatCurrency(localInvoice.total_payments || 0, localInvoice.currency)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Payments
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#e8f5e8' }}>
              <Typography variant="h6" color="success.main">
                {formatCurrency(localInvoice.net_amount || 0, localInvoice.currency)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Net Amount
              </Typography>
            </Paper>
          </Box>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {onGenerate && (
          <Button 
            variant="contained" 
            onClick={onGenerate}
            disabled={!localInvoice || localInvoice.items?.length === 0}
          >
            Generate Invoice
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InvoicePreview;
